// /projects — list, create, switch, delete projects.
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useProject } from "../contexts/ProjectContext";
import { listProjects, deleteProject } from "../services/projects";
import { listOrgs } from "../services/orgs";
import CreateProjectModal from "../components/projects/CreateProjectModal";
import { listBranches } from "../services/branches";
import { FolderOpen, Plus, Trash2, CheckCircle2, X, Loader2, Copy, CheckCheck, Terminal, Sparkles, FilePlus2, Building2, Bot, Wrench, History } from "lucide-react";
import AppRequestDialog from "../components/AppRequestDialog";
import VersionHistory from "../components/VersionHistory";

export default function Projects() {
  const { user, switchProject } = useAuth();
  const { projectSlug: activeProject } = useProject();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showChooser, setShowChooser] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showChange, setShowChange] = useState(false);
  // null | 'new' | 'change' — which flow filed a request, so the confirmation
  // toast can say something true about what happens next.
  const [assistantFiled, setAssistantFiled] = useState(null);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [mcpProject, setMcpProject] = useState(null);
  const [historyProject, setHistoryProject] = useState(null);

  // Acting-org scope (platform admins only): narrows the cross-org god view
  // to one organization. The selection persists in localStorage and rides on
  // every API call as X-Cortex-Org (see fetchapi.js); the backend applies it
  // as a visibility filter for platform admins and ignores it for everyone
  // else. "" = All organizations (the legacy merged view).
  const isPlatformAdmin = !!user?.orgs?.some((o) => o.role === "platform_admin");
  // Admins (platform or org) get the third tab: every project of the
  // organization. They don't need an explicit grant to see them — that is what
  // administering an org means — so those rows would otherwise be
  // indistinguishable from the user's own.
  const isOrgAdmin = !!user?.orgs?.some((o) => o.role === "org_admin");
  const canSeeOrgTab = isPlatformAdmin || isOrgAdmin;
  const [orgs, setOrgs] = useState([]);
  const [actingOrg, setActingOrg] = useState(() => localStorage.getItem("cortex_acting_org") || "");
  const [tab, setTab] = useState("mine");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      setProjects(await listProjects());
    } catch (e) {
      setError(e?.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [actingOrg]);
  useEffect(() => {
    if (!isPlatformAdmin) return;
    listOrgs().then(setOrgs).catch(() => setOrgs([]));
  }, [isPlatformAdmin]);

  const changeActingOrg = (slug) => {
    if (slug) localStorage.setItem("cortex_acting_org", slug);
    else localStorage.removeItem("cortex_acting_org");
    setActingOrg(slug);
  };

  // Partition by the backend's per-row `access` descriptor ('owner' |
  // 'shared' | 'organization'). Older payloads without it fall back to
  // is_owner/shared_with_me so the page still groups sensibly.
  const buckets = useMemo(() => {
    const mine = [], shared = [], organization = [];
    for (const p of projects) {
      const access = p.access || (p.is_owner ? "owner" : p.shared_with_me ? "shared" : "organization");
      if (access === "owner") mine.push(p);
      else if (access === "shared") shared.push(p);
      else organization.push(p);
    }
    return { mine, shared, organization };
  }, [projects]);

  // "My projects" means projects the user OWNS — never org-visible ones they
  // merely have access to (calling those "mine" reads as ownership and, for a
  // brand-new user who owns nothing, is plainly wrong). Org-visible rows go to
  // the Organization tab, which is shown to everyone who has any; admins see it
  // because they reach every project of their org that way.
  const TABS = useMemo(() => {
    const base = [
      { key: "mine", label: "My projects", rows: buckets.mine },
      { key: "shared", label: "Shared with me", rows: buckets.shared },
    ];
    if (canSeeOrgTab || buckets.organization.length) {
      base.push({ key: "organization", label: "Organization projects", rows: buckets.organization });
    }
    return base;
  }, [buckets, canSeeOrgTab]);

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];
  const visibleProjects = activeTab?.rows || [];

  const handleSwitch = async (slug) => {
    // switchProject navigates to /p/<slug>/b/main/... (URL is the source of truth).
    // Do NOT reload here — a reload would cancel the navigation and keep the
    // current project.
    const res = await switchProject(slug);
    if (!res.success) alert(`Switch failed: ${res.error}`);
  };

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Projects</h1>
          <p className="text-sm font-normal text-page-muted">
            Each project has its own data sources, mappings, processing rules, reference lists, and pipeline. Use projects to isolate different ETL initiatives.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          {isPlatformAdmin && orgs.length > 0 && (
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-[5px] bg-card-bg text-sm text-page-text" title="Platform admin: scope the view to one organization">
              <Building2 size={16} className="text-page-muted" />
              <select
                value={actingOrg}
                onChange={(e) => changeActingOrg(e.target.value)}
                className="bg-transparent text-sm outline-none cursor-pointer"
              >
                <option value="">All organizations</option>
                {orgs.map((o) => (
                  <option key={o.slug} value={o.slug}>{o.name || o.slug}</option>
                ))}
              </select>
            </label>
          )}
          {activeProject ? (
            <span className="inline-flex items-center gap-2 h-9 px-3 rounded-[5px] bg-card-bg text-sm text-page-text">
              <FolderOpen size={16} />
              Active: <strong className="font-semibold">{activeProject}</strong>
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 h-9 px-3 rounded-[5px] bg-card-bg text-sm text-page-muted">
              <FolderOpen size={16} />
              No active project
            </span>
          )}
          {/* Changing the ACTIVE project is its own top-level action — it is
              not a kind of "new project", and burying it under that button is
              how it used to be unreachable. Secondary styling keeps New
              Project the primary CTA. */}
          {activeProject && (
            <button
              onClick={() => setShowChange(true)}
              data-testid="change-project"
              title={`Ask an agent to change ${activeProject}`}
              className="flex items-center gap-2 h-9 px-4 rounded-[5px] border border-card-border bg-card-bg text-page-text text-sm font-medium cursor-pointer hover:bg-page-bg transition-colors"
            >
              <Wrench size={18} /> Change this project
            </button>
          )}
          <button
            onClick={() => setShowChooser(true)}
            data-testid="new-project"
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <Plus size={20} /> New Project
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-800 px-4 py-2 rounded-[5px] text-sm">{error}</div>}

      {!loading && projects.length === 0 && (
        <div className="bg-card-bg rounded-[8px] p-10 flex flex-col items-center gap-4 text-center">
          <FolderOpen size={48} className="text-page-muted" />
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-medium text-page-title">No projects yet</h2>
            <p className="text-sm text-page-muted max-w-md">
              A project bundles your sources, mappings, processing rules, and pipeline runs. Create one to get started — you can have as many as you need, each isolated from the others.
            </p>
          </div>
          <button
            onClick={() => setShowChooser(true)}
            className="flex items-center gap-2 h-10 px-5 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <Plus size={20} /> Create your first project
          </button>
        </div>
      )}

      {!loading && projects.length > 0 && (
      <div className="flex items-center gap-1 border-b border-page-divider">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`relative -mb-px px-4 h-9 text-sm rounded-t-[5px] border-b-2 cursor-pointer bg-transparent transition-colors ${
              activeTab?.key === t.key
                ? "border-nav-active text-page-title font-medium"
                : "border-transparent text-page-muted hover:text-page-text"
            }`}
          >
            {t.label}
            <span className="ml-2 text-xs text-page-muted">{t.rows.length}</span>
          </button>
        ))}
      </div>
      )}

      {!loading && projects.length > 0 && (
      <div className="bg-card-bg rounded-[8px] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-page-muted">
            <tr className="border-b border-page-divider">
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Slug</th>
              <th className="px-5 py-3 font-medium">Organization</th>
              <th className="px-5 py-3 font-medium">Description</th>
              <th className="px-5 py-3 font-medium">Created</th>
              <th className="px-5 py-3 font-medium w-[180px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-6 text-center text-page-muted">
                <Loader2 className="inline animate-spin mr-2" size={16}/> Loading…
              </td></tr>
            ) : visibleProjects.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-6 text-center text-page-muted">
                {activeTab?.key === "shared"
                  ? "Nothing has been shared with you yet."
                  : activeTab?.key === "organization"
                    ? "This organization has no other projects."
                    : "No projects."}
              </td></tr>
            ) : visibleProjects.map(p => {
              const isActive = p.slug === activeProject;
              const isDefault = p.slug === 'default';
              // Reached ONLY through a shared app/agent: the project itself is
              // not open to them, so switching into it would 403 on every page.
              // Their entry point is the app, from the Apps page of that share.
              const surfaceOnly = !!p.shared_surface_only;
              return (
                <tr key={p.slug} className="border-b border-page-divider last:border-b-0 hover:bg-page-bg/40">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <FolderOpen size={16} className="shrink-0 text-page-muted" />
                      <span className="font-medium">{p.name}</span>
                      {isActive && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                          <CheckCircle2 size={12}/> active
                        </span>
                      )}
                      {isDefault && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">default</span>
                      )}
                      {surfaceOnly && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"
                          title="Only specific apps or agents in this project were shared with you"
                        >
                          shared items only
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-page-muted">{p.slug}</td>
                  <td className="px-5 py-3 text-xs text-page-muted">{p.org_slug || "mindshine"}</td>
                  <td className="px-5 py-3 text-page-muted truncate max-w-[400px]">{p.description || "—"}</td>
                  <td className="px-5 py-3 text-page-muted text-xs">{p.created_by} · {new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      {surfaceOnly ? (
                        // The only thing they can open is the shared surface.
                        <a
                          href={`/p/${encodeURIComponent(p.slug)}/b/main/apps`}
                          className="px-2 py-1 rounded text-xs hover:bg-page-bg text-page-text no-underline"
                        >
                          Open shared apps
                        </a>
                      ) : (
                        <>
                          {!isActive && (
                            <button onClick={() => handleSwitch(p.slug)} className="px-2 py-1 rounded text-xs hover:bg-page-bg">Switch</button>
                          )}
                          {/* A project IS its paired Flow fork, so its history is
                              the fork's export commits — every commit is a version
                              of the whole project, unfiltered. */}
                          <button
                            onClick={() => setHistoryProject(p)}
                            data-testid={`project-history-${p.slug}`}
                            className="p-1.5 rounded hover:bg-page-bg text-page-muted"
                            title="Version history"
                          >
                            <History size={16}/>
                          </button>
                          <button
                            onClick={() => setMcpProject(p)}
                            className="p-1.5 rounded hover:bg-page-bg text-page-muted"
                            title="MCP configuration for external agents"
                          >
                            <Terminal size={16}/>
                          </button>
                          {!isDefault && !isActive && (
                            <button onClick={() => setSelectedForDelete(p.slug)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete">
                              <Trash2 size={16}/>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {showChooser && (
        <NewProjectChooser
          onClose={() => setShowChooser(false)}
          onBlank={() => { setShowChooser(false); setShowCreate(true); }}
          onGuided={() => {
            setShowChooser(false);
            // No slug here — the guided panel asks the user to name the project
            // as one of its steps. Nothing about the data is predetermined.
            window.dispatchEvent(new CustomEvent("cortex:start-guided-demo", { detail: {} }));
          }}
          onAssistant={() => { setShowChooser(false); setShowAssistant(true); }}
        />
      )}
      {showAssistant && (
        <AppRequestDialog
          kinds={NEW_PROJECT_KINDS}
          title="Project assistant"
          onClose={() => setShowAssistant(false)}
          onCreated={() => { setShowAssistant(false); setAssistantFiled("new"); }}
        />
      )}
      {showChange && (
        <AppRequestDialog
          kinds={CHANGE_PROJECT_KINDS}
          title={`Change ${activeProject}`}
          onClose={() => setShowChange(false)}
          onCreated={() => { setShowChange(false); setAssistantFiled("change"); }}
        />
      )}
      {assistantFiled && (
        <div className="fixed bottom-5 right-5 z-50 rounded-lg border border-card-border bg-card-bg shadow-lg px-4 py-3 text-sm text-page-text flex items-center gap-3">
          <CheckCircle2 size={16} className="text-green-600" />
          {assistantFiled === "change" ? (
            <span>Change request filed — an agent is on it. Track progress on the <b>Board</b>; the changes land in this project when it's done.</span>
          ) : (
            <span>Project request filed — an agent is on it. Track progress on the <b>Board</b>; the new project appears here when it's ready.</span>
          )}
          <button onClick={() => setAssistantFiled(null)} className="text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1"><X size={14} /></button>
        </div>
      )}
      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onSuccess={async (createdSlug) => {
            setShowCreate(false);
            await load();
            // Auto-switch into the new project so the user immediately
            // works inside it (chat panel reconnects, pipeline/Projects
            // pages re-render bound to the new active_project).
            if (createdSlug) {
              try { await switchProject(createdSlug); } catch { /* surface in UI later */ }
            }
          }}
        />
      )}
      {selectedForDelete && (
        <DeleteProjectDialog
          slug={selectedForDelete}
          onClose={() => setSelectedForDelete(null)}
          onSuccess={() => { setSelectedForDelete(null); load(); }}
        />
      )}
      {mcpProject && (
        <McpConfigModal
          project={mcpProject}
          isActive={mcpProject.slug === activeProject}
          onClose={() => setMcpProject(null)}
        />
      )}
      {historyProject && (
        <ModalShell title={`Version history — ${historyProject.name || historyProject.slug}`}
                    size="lg" onClose={() => setHistoryProject(null)}>
          {/* The panel reads the project in scope from the request header, so it
              must be told which project this row is — not the active one. */}
          <VersionHistory kind="project" name={historyProject.slug}
                          projectSlug={historyProject.slug} canRestore />
        </ModalShell>
      )}
    </div>
  );
}

// Modal: shows a copy-pasteable .mcp.json snippet for the selected project.
// External agents (Claude Code, Cursor, etc.) drop this into their
// project-local .mcp.json. Cortex MCP exposes both the recipe tools and the
// paired Flow project's tools (under `flow__*`) on a single endpoint and
// gates them with OAuth 2.0 — the agent's first request triggers the
// browser-based consent flow against cortex.
function McpConfigModal({ project, isActive, onClose }) {
  // The URL uses the same origin the user is viewing the UI from. Nginx (or
  // Vite in dev) proxies /mcp/* to the assistant process — users don't need
  // to know the internal port. Branch is loaded live from the per-project
  // branches API.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [branch, setBranch] = useState("main");
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setBranchesLoading(true);
    (async () => {
      try {
        const list = await listBranches(project.slug);
        const names = list.map(b => typeof b === 'string' ? b : b.name).filter(Boolean);
        if (!cancelled) {
          setBranches(names);
          // Default to main if available; otherwise first branch.
          if (names.length > 0 && !names.includes(branch)) {
            setBranch(names.includes('main') ? 'main' : names[0]);
          }
        }
      } catch {
        if (!cancelled) setBranches([]);
      } finally {
        if (!cancelled) setBranchesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.slug]);

  const url = `${origin}/mcp/p/${project.slug}/b/${branch}`;
  const snippet = JSON.stringify({
    mcpServers: {
      [`cortex-${project.slug}`]: {
        type: "http",
        url,
      },
    },
  }, null, 2);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <ModalShell title={`MCP configuration — ${project.name}`} onClose={onClose}
      footer={
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Close</button>
      }>
      <div className="flex flex-col gap-3 text-sm">
        <p className="text-page-muted">
          Connect any MCP-compatible agent (Claude Code, Cursor, OpenCode, …) to this project. The agent gets the full set of tools for configuring and running the pipeline.
        </p>
        <p className="text-page-muted">
          On first connect, your agent opens a browser tab so you can sign in to cortex. Tokens are cached for an hour — you won't be prompted again until they expire.
        </p>
        <label className="block">
          <span className="block text-xs text-page-muted mb-1">Branch</span>
          <select
            value={branch}
            onChange={e => setBranch(e.target.value)}
            disabled={branchesLoading || branches.length === 0}
            className="w-full max-w-[260px] h-8 px-2 rounded border border-page-divider bg-page-bg text-xs font-mono disabled:opacity-60"
          >
            {branchesLoading && <option value={branch}>Loading…</option>}
            {!branchesLoading && branches.length === 0 && <option value={branch}>{branch}</option>}
            {!branches.includes(branch) && !branchesLoading && branches.length > 0 && (
              <option value={branch}>{branch}</option>
            )}
            {branches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        <div className="relative">
          <pre className="bg-page-bg text-page-text rounded p-3 pr-12 text-xs font-mono overflow-x-auto whitespace-pre">{snippet}</pre>
          <button
            onClick={copy}
            className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded bg-card-bg border border-page-divider text-xs hover:bg-page-bg"
            title="Copy snippet"
          >
            {copied ? <><CheckCheck size={12}/> Copied</> : <><Copy size={12}/> Copy</>}
          </button>
        </div>
        <details className="text-xs text-page-muted">
          <summary className="cursor-pointer">How to use this</summary>
          <ol className="list-decimal ml-5 mt-2 space-y-1">
            <li>Save the snippet to <code className="font-mono">.mcp.json</code> at the root of the directory you want the agent to operate in.</li>
            <li>Start the agent there (e.g. <code className="font-mono">claude</code> from Claude Code, or your IDE's MCP launcher).</li>
            <li>On first connect, a browser tab opens — sign in to cortex to authorize the agent.</li>
          </ol>
        </details>
      </div>
    </ModalShell>
  );
}

// The shell CAPS ITS OWN HEIGHT and scrolls the BODY, keeping the title bar and
// footer pinned. Without the cap a long list (a project's 25-commit history) grew
// the dialog past the viewport: the header scrolled off the top and the last rows
// were unreachable, because the page behind a `fixed` overlay does not scroll.
// `size` widens it for content that needs more room than a form field.
function ModalShell({ title, onClose, children, footer, size = "md" }) {
  const maxW = size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4">
      <div className={`bg-card-bg rounded-[8px] shadow-xl w-full ${maxW} mt-12 md:mt-0 flex flex-col max-h-[85vh]`}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-page-divider shrink-0">
          <h2 className="text-lg font-semibold truncate pr-2">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-page-bg shrink-0"><X size={18}/></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto min-h-0">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-page-divider shrink-0">{footer}</div>}
      </div>
    </div>
  );
}

// Chooser shown by "New Project": start blank, or run a Guided walkthrough that
// builds a complete pipeline live via the Cortex assistant — you name the
// project and bring your own data; nothing about the domain is predetermined.
function NewProjectChooser({ onClose, onBlank, onGuided, onAssistant }) {
  return (
    <ModalShell title="New project" onClose={onClose}
      footer={<button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>}>
      <div className="flex flex-col gap-3">
        <button onClick={onBlank}
          className="flex items-start gap-3 text-left p-3 rounded-[8px] border border-page-divider hover:border-nav-active hover:bg-page-bg/50 transition-colors">
          <FilePlus2 size={22} className="shrink-0 mt-0.5 text-page-muted" />
          <div>
            <div className="font-medium text-sm">Blank project</div>
            <div className="text-xs text-page-muted">Create an empty project and configure sources, mappings, and the pipeline yourself.</div>
          </div>
        </button>
        <button onClick={onGuided}
          className="flex items-start gap-3 text-left p-3 rounded-[8px] border border-page-divider hover:border-nav-active hover:bg-page-bg/50 transition-colors">
          <Sparkles size={22} className="shrink-0 mt-0.5 text-nav-active" />
          <div>
            <div className="font-medium text-sm">Guided</div>
            <div className="text-xs text-page-muted">Build a complete governed pipeline live, step by step, with the assistant. You name the project and upload your own data; a slide panel walks each step — drive it with Next or let it auto-play.</div>
          </div>
        </button>
        <button onClick={onAssistant} data-testid="new-project-assistant"
          className="flex items-start gap-3 text-left p-3 rounded-[8px] border border-page-divider hover:border-nav-active hover:bg-page-bg/50 transition-colors">
          <Bot size={22} className="shrink-0 mt-0.5 text-nav-active" />
          <div>
            <div className="font-medium text-sm">Project assistant</div>
            <div className="text-xs text-page-muted">Describe the project — or attach a document — and an agent builds the whole thing: data sources, data elements, mappings, a pipeline that runs, workflows with AI agents, and an app. Track progress on the Board.</div>
          </div>
        </button>
      </div>
    </ModalShell>
  );
}

// The project-assistant request form: ONE kind (new_project), plain-language
// brief + optional attached document. Files as an app_request; the fleet's
// project assistant builds the complete project and the Board tracks it.
//
// `change_project` deliberately does NOT live here. It is about the ACTIVE
// project, not about creating one, so it has its own top-level "Change this
// project" button on this page (see CHANGE_PROJECT_KINDS below) instead of
// hiding as a second tab inside the New Project flow.
export const NEW_PROJECT_KINDS = [
  {
    kind: "new_project",
    label: "New project",
    icon: Bot,
    hint: "Describe the business domain and what the project should do — the assistant designs the dataset, governs it end to end (sources → elements → mappings → pipeline), publishes the workflows with AI agents and human approvals, and builds the app.",
    titlePlaceholder: "What should the project be called / be about?",
    detailsPlaceholder: "Describe the domain in plain language — the data involved, the process to automate, who approves what… or attach a document below.",
    allowFile: true,
    tenancyChoice: true, suggestFeatures: true,
    fileHint: "Attach the domain documents, data archives, or images (.docx, .txt, .md, .csv, .xlsx, .zip, .png, .jpg)",
  },
];

// "Change this project" — reached from its OWN button next to New Project. The
// request is filed against the project it is submitted from (the backend
// resolves change_project's target to the request's own project_slug), so
// there is nothing to pick: one kind, brief + optional attachment.
export const CHANGE_PROJECT_KINDS = [
  {
    kind: "change_project",
    label: "Change this project",
    icon: Wrench,
    hint: "Describe what should change in this project — new or updated data, extra workflow steps, app changes… The assistant modifies the existing project in place and reports what it changed.",
    titlePlaceholder: "Short summary of the change",
    detailsPlaceholder: "Describe the change in plain language — what data, workflows, or app behavior should be different… or attach new data files below.",
    allowFile: true,
    fileHint: "Attach a document, new data files, or an image (.docx, .txt, .md, .csv, .xlsx, .zip, .png, .jpg)",
  },
];

// CreateProjectModal moved to components/projects/CreateProjectModal.jsx so the
// no-projects first-run screen (RedirectToFirstProject) can reuse it — that
// screen renders outside the /p/<slug> routes where this page isn't reachable.

function DeleteProjectDialog({ slug, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setSubmitting(true); setErr(null);
    try { await deleteProject(slug); onSuccess(); }
    catch (e) { setErr(e?.message || "Delete failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <ModalShell title={`Delete ${slug}`} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-3 py-1.5 rounded text-sm hover:bg-page-bg">Cancel</button>
        <button disabled={submitting} onClick={submit} className="px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium disabled:opacity-50">
          {submitting ? "Deleting…" : "Delete"}
        </button>
      </>}>
      <p className="text-sm">Permanently delete project <strong>{slug}</strong> and its Dolt database? This is destructive.</p>
      {err && <div className="text-sm text-red-700 mt-3">{err}</div>}
    </ModalShell>
  );
}
